/**
 * A responsive data table with sortable columns (server- or client-side) and
 * optional pagination. On small screens it scrolls horizontally.
 */
import type { ReactNode } from "react";
import { Button, Table } from "react-bootstrap";
import { useNavigate } from "react-router";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Sort key sent to the server (or used for client-side sorting with `sortValue`). */
  sort?: string;
  sortValue?: (row: T) => string | number | null;
  align?: "end" | "center";
  className?: string;
}

export interface SortState {
  sort: string | null;
  dir: "asc" | "desc";
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  rowLink,
  sort,
  onSort,
  clientSort,
  footer,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  rowLink?: (row: T) => string;
  sort?: SortState;
  onSort?: (s: SortState) => void;
  /** Sort rows in the browser using each column's sortValue (instead of on the server). */
  clientSort?: boolean;
  footer?: ReactNode;
  empty?: ReactNode;
}) {
  const navigate = useNavigate();
  let data = rows;
  if (clientSort && sort?.sort) {
    const col = columns.find((c) => c.sort === sort.sort);
    if (col?.sortValue) {
      const dir = sort.dir === "desc" ? -1 : 1;
      data = [...rows].sort((a, b) => {
        const av = col.sortValue!(a);
        const bv = col.sortValue!(b);
        if (av === bv) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return av < bv ? -dir : dir;
      });
    }
  }
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className="table-responsive">
      <Table hover className="align-middle mb-0 data-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.sort === c.sort && c.sort;
              const alignClass = c.align ? `text-${c.align}` : "";
              return (
                <th
                  key={c.key}
                  className={`${alignClass} ${c.className ?? ""}`}
                  scope="col"
                  aria-sort={active ? (sort?.dir === "desc" ? "descending" : "ascending") : undefined}
                >
                  {c.sort && onSort ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-reset text-decoration-none fw-semibold sort-button"
                      onClick={() => {
                        const next: SortState = {
                          sort: c.sort!,
                          dir: active && sort?.dir === "asc" ? "desc" : "asc",
                        };
                        onSort(next);
                      }}
                    >
                      {c.header}
                      <span className="ms-1 text-muted small">
                        {active ? (sort?.dir === "desc" ? "▼" : "▲") : "↕"}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={rowKey(row)}
              className={rowLink ? "clickable-row" : undefined}
              onClick={
                rowLink
                  ? (e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,label")) return;
                      navigate(rowLink(row));
                    }
                  : undefined
              }
            >
              {columns.map((c) => (
                <td key={c.key} className={`${c.align ? `text-${c.align}` : ""} ${c.className ?? ""}`}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </Table>
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center p-3 border-top small">
      <span className="text-muted">
        Page {page} of {pages} · {total} records
      </span>
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
